import { html } from 'lit-element';
import { nothing } from 'lit-html';
import { classMap } from 'lit-html/directives/class-map.js';
import {
  KatalComponent,
  Keys,
  KatLitElement,
  booleanConverter,
  coerceBooleanClassProperties,
} from '../../shared/base';
import strings from './strings';

const MAX_FEEDBACK_TEXT_LENGTH = 500;
const EMOJI_STRING_MAP = {
  1: 'very_unsatisfied_option',
  2: 'unsatisfied_option',
  3: 'neutral_option',
  4: 'satisfied_option',
  5: 'very_satisfied_option',
};

/**
 * @component {kat-hmd} KatalHmd HMD (Satisfaction Survey) component, behaving as a modal.
 * @guideline Do Use to collect customer opinions on experiences or services
 * @guideline Do Listen to the submit event to handle HMD submissions. Use the error attribute to communicate submission errors to the user.
 * @guideline Dont Modify question prompts or HTML inside of the component
 * @guideline Dont Listen to the cancel button for click events. This button has been pre-wired to close the component.
 * @example Sample {"visible": false, "locale":"en-US", "rating":"0", "comment":"", "error":""}
 * @example Localized {"visible": false, "locale": "ja-JP", "rating":"0", "comment":"", "error":""}
 * @example Error {"visible": false, "locale":"en-US", "error": "Failed to submit your feedback. Please try again.", "rating":"3", "comment":"Your website is average."}
 * @example WithStarRating {"visible": false, "locale": "en-US", "rating":"0", "comment":"", "error":"", "ratingStyle":"stars"}
 * @example WithNumericalRating {"visible": false, "locale": "en-US", "rating":"0", "comment":"", "error":"", "ratingStyle":"numerical"}
 * @event submit Fired when the HMD is submitted by the user. the details object on the event will contain rating and comment values.
 * @event dismissed Fired when the HMD is Cancelled or otherwise hidden
 * @event opened Fired when the HMD Modal Dialog is opened
 * @status Production
 */
export class KatalHmd extends KatLitElement {
  static get properties() {
    return {
      /**
       * @param {boolean} visible Determines if the HMD is visible to the user
       */
      visible: { type: Boolean, converter: booleanConverter, reflect: true },

      /**
       * @param {string} error Error text to display
       */
      error: { type: String, reflect: true },

      /**
       * @param {string} comment Feedback provided by the user
       */
      comment: { type: String, reflect: true },

      /**
       * @param {number} rating Numeric HMD sentiment rating (0-5), defaults to 0 for unselected
       */
      rating: { type: Number, reflect: true },

      /**
       * @param {string} locale Locale for the prompt text
       */
      locale: { type: String, reflect: true },

      /**
       * @param {enum} state Show success message instead of form
       * @enum {value} normal Shows the HMD form
       * @enum {value} busy Shows the HMD form, covered by a loading spinner to show that submission is in progress
       * @enum {value} success Shows a thank you message.
       */
      state: { type: String, reflect: true },

      /**
       * @param {string} subject Subject provided by the user
       */
      subject: { type: String, reflect: true },

      /**
       * @param {enum} mode This is the mode of the HMD - Default is modal
       * @enum {value} modal HMD will load as a modal - Default
       * @enum {value} inline HMD will load as an inline form
       */
      mode: { type: String, reflect: true },

      /**
       * @param {enum} ratingStyle This is the style of the HMD rating- Default is emoji
       * @enum {value} emoji rating use emoji to rate - Default
       * @enum {value} stars rating use stars to rate
       * @enum {value} numerical rating use numbers to rate
       */
      ratingStyle: { type: String, attribute: 'rating-style', reflect: true },
    };
  }

  constructor() {
    super();
    this.visible = false;
    this.mode = 'modal';
    this.error = '';
    this.locale = 'en-US';
    this.ratingStyle = 'emoji';
    this.comment = '';
    this.rating = 0;
    this.state = '';
    this.subject = '';
    this._boundFormEvents = [
      'change',
      'input',
      'keydown',
      'keyup',
      'click',
      'focus',
      'blur',
      'dismissed',
    ];
    this._actionableKeyCodes = [Keys.Enter, Keys.Space];
    this.ratingScore = [1, 2, 3, 4, 5];
  }

  // disable shadow-dom
  createRenderRoot() {
    return this;
  }

  updated(changedProperties) {
    if (!(changedProperties.has('mode') && changedProperties.size === 1)) {
      this.dispatchCustomEvent('changed', { value: this.value }, false);
    }

    if (this.state !== 'success' && this.state !== 'busy') {
      this.querySelector(
        '.kat-charcount'
      ).innerHTML = this.getCharsRemainingText(this.comment.length);
    }
  }

  firstUpdated() {
    this.registerEvents();
  }

  close() {
    this.removeAttribute('visible');
    this.dispatchCustomEvent('dismissed', { visible: this.visible });
  }

  open() {
    this.setAttribute('visible', '');
    this.dispatchCustomEvent('opened', { visible: this.visible });
  }

  reset() {
    this.close();
    this.comment = '';
    this.rating = 0;
    this.subject = '';
    this.state = 'normal';
    this.error = '';
  }

  succeed() {
    this.state = 'success';
  }

  render() {
    const visible = this.visible;
    const renderContent = this.getContent();

    if (this.mode === 'modal') {
      return html`
        <kat-modal class="hmd-container-div" ?visible=${visible}>
          ${renderContent}
        </kat-modal>
      `;
    }
    return html`
      <div class="hmd-container-div">
        ${renderContent}
      </div>
    `;
  }

  getContent() {
    const rating = this.rating;
    const khmdfooterClass =
      this.state === 'success' || this.state === 'busy' ? 'kat-hmd-hide' : '';
    const cancelBtn = html`
      <kat-button
        class="katHmdCancelBtn"
        variant="secondary"
        label=${this.getString('cancel_label')}
        @click=${this.cancelBtnClick}
      ></kat-button>
    `;
    const submitBtn = html`
      <kat-button
        class="katHmdSubmitBtn"
        label=${this.getString('submit_label')}
        ?disabled=${rating === 0}
        @click=${this.submitBtnClick}
      ></kat-button>
    `;

    return html`
      <kat-modal-content>
        <kat-modal-header>${this.getString('modal_title')}</kat-modal-header>

        <kat-modal-body>${this.getKatModalBody(this.state)}</kat-modal-body>

        <kat-modal-footer class=${khmdfooterClass}>
          ${cancelBtn} ${submitBtn}
        </kat-modal-footer>
      </kat-modal-content>
    `;
  }

  getKatModalBody(state) {
    const { error, rating } = this;

    if (state === 'success') {
      return html`
        <p class="khmdSuccess">${this.getString('thanks')}</p>
      `;
    }
    if (state === 'busy') {
      return html`
        <kat-spinner></kat-spinner>
      `;
    }
    if (state !== 'success' && this.state !== 'busy') {
      const getLabel = html`
        <kat-label text=${this.getString('rating_prompt')}></kat-label>
      `;
      const getText = html`
        <div class="kat-text-right kat-charcount"></div>
      `;
      const getAlert = error.length
        ? html`
            <kat-alert variant="danger" description="${error}"></kat-alert>
          `
        : nothing;

      return html`
        ${getAlert} ${getLabel} ${this.getSatisfactionRatingOptions(rating)}
        ${this.getDropDown()} ${this.getTextarea()}

        <div class="content kat-row kat-no-gutters">
          <div class="kat-disclaimer">
            <span
              >${this.getString('popup_prompt')}
              <kat-tooltip
                trigger-icon="info-circle"
                size="small"
                label=${this.getString('disclaimer')}
                position="top"
              ></kat-tooltip>
            </span>
          </div>
          ${getText}
        </div>
      `;
    }
  }

  getTextarea() {
    return html`
      <kat-textarea
        state="default"
        maxlength=${MAX_FEEDBACK_TEXT_LENGTH}
        value=${this.comment}
        label=${this.getString('comment_prompt')}
        placeholder=${this.getString('textarea_placeholder')}
      ></kat-textarea>
    `;
  }

  getDropDown() {
    const subject = this.subject;
    const options = JSON.stringify([
      {
        name: this.getString('subject_suggestion'),
        value: 'suggestion',
      },
      {
        name: this.getString('subject_technical'),
        value: 'issue',
      },
      {
        name: this.getString('subject_compliment'),
        value: 'compliment',
      },
      {
        name: this.getString('subject_other'),
        value: 'other',
      },
    ]);

    return html`
      <kat-dropdown
        class="kat-subject-dropdown"
        label=${this.getString('subject_prompt')}
        options=${options}
        placeholder=${this.getString('subject_select')}
        value=${subject}
      ></kat-dropdown>
    `;
  }

  getSatisfactionRatingOptions(rating) {
    if (this.ratingStyle === 'emoji') {
      return this.getEmojiRating(rating);
    }
    if (this.ratingStyle === 'numerical') {
      return this.getNumRating(rating);
    }
    if (this.ratingStyle === 'stars') {
      return this.getStarRating(rating);
    }
  }

  getStarRating(rating) {
    return html`
      <ul class="kat-satisfaction-rating">
        <kat-star-rating
          class="hmd2-star"
          variant="input"
          name="starrating"
          rating=${rating}
        ></kat-star-rating>
      </ul>
    `;
  }

  getEmojiRating(rating) {
    let ratingContent = [];
    ratingContent = this.ratingScore.map(num => {
      const classes = { 'hmd2-emoji': true, selected: rating == num };
      const key = 'hmd2-emoji-' + num;
      classes[key] = true;

      return html`
        <li class=${'rating-' + num}>
          <a
            class=${classMap(classes)}
            tabindex="0"
            data-rating=${num}
            aria-label=${this.getString(EMOJI_STRING_MAP[num])}
          ></a>
        </li>
      `;
    });
    return html`
      <ul class="kat-satisfaction-rating">
        ${ratingContent}
      </ul>
    `;
  }

  getNumRating(rating) {
    let ratingContent = [];
    ratingContent = this.ratingScore.map(num => {
      const classes = {
        'hmd2-number-rating': true,
        'hmd2-number': true,
        selected: rating == num,
      };

      return html`
        <li class=${'numrating-' + num}>
          <div
            class=${classMap(classes)}
            tabindex="0"
            data-rating=${num}
            aria-label=${this.getString(EMOJI_STRING_MAP[num])}
          >
            <span
              class="hmd2-number-rating"
              data-rating=${num}
              aria-hidden="true"
              >${num}</span
            >
          </div>
          ${num === 1 || num === 3 || num === 5
            ? html`
                <p class="hmd2-number-text" aria-hidden="true">
                  ${this.getString(EMOJI_STRING_MAP[num])}
                </p>
              `
            : nothing}
        </li>
      `;
    });
    return html`
      <ul class="kat-satisfaction-rating">
        ${ratingContent}
      </ul>
    `;
  }

  cancelBtnClick() {
    this.reset();
    this.close();
  }

  submitBtnClick() {
    this.dispatchCustomEvent(
      'submit',
      {
        rating: this.rating,
        comment: this.comment,
        subject: this.subject,
      },
      false
    );
  }

  registerEvents() {
    const form = this.querySelector('.hmd-container-div');

    this._boundFormEvents.forEach(name => {
      form.addEventListener(name, e => {
        this.handleEvent(e);
      });
    });
  }

  isKeycodeActionable(keyCode) {
    return this._actionableKeyCodes.includes(keyCode);
  }

  handleEvent(event) {
    if (event.target.classList.contains('hmd2-emoji')) {
      if (
        event.type === 'click' ||
        (event.type === 'keyup' && this.isKeycodeActionable(event.keyCode))
      ) {
        this.rating = event.target.getAttribute('data-rating');
      }
    } else if (event.target.classList.contains('hmd2-star')) {
      if (event.type === 'change') {
        this.rating = event.detail.rating;
      }
    } else if (event.target.matches('.hmd2-number-rating')) {
      if (
        event.type === 'click' ||
        (event.type === 'keyup' && this.isKeycodeActionable(event.keyCode))
      ) {
        this.rating = event.target.getAttribute('data-rating');
      }
    } else if (event.target.tagName === 'KAT-DROPDOWN') {
      if (event.type === 'change') {
        this.subject = event.target.value;
      }
    } else if (event.target.tagName === 'KAT-TEXTAREA') {
      if (event.type === 'change') {
        const inputComment = event.detail.value;
        const currentComment = this.comment;
        if (inputComment !== currentComment) {
          this.comment = inputComment;
        }
      }
      if (event.type === 'input') {
        this.querySelector(
          '.kat-charcount'
        ).innerHTML = this.getCharsRemainingText(event.detail.value.length);
      }
    } else if (event.target.tagName === 'KAT-MODAL') {
      if (event.type === 'dismissed') {
        this.visible = false;
      }
    }
  }

  getCharsRemainingText(commentLength) {
    const text = ' ' + this.getString('chars_remaining');
    return 500 - commentLength + text;
  }

  getString(stringId) {
    const locale = this.locale;
    let string = strings[stringId][locale];
    if (!string) {
      string = strings[stringId]['en-US'];
    }
    return string;
  }
}
coerceBooleanClassProperties(KatalHmd);
KatalComponent.register('kat-hmd', KatalHmd);
